import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  User, Calendar, FileText, MessageSquare, Settings, Bell, Target,
  Clock, MapPin, Users, Trophy, Activity, BookOpen, Edit, Save, X,
  Smile, Frown, CreditCard, AlertTriangle, CheckCircle, Star
} from 'lucide-react';
import axios from 'axios';

const PlayersArea = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [playerData, setPlayerData] = useState(null);
  const [playerStats, setPlayerStats] = useState(null);
  const [playerPayments, setPlayerPayments] = useState([]);
  const [coachFeedback, setCoachFeedback] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({});

  const clubLogo = "https://customer-assets.emergentagent.com/job_cafc-platform/artifacts/d9rcitex_OurLogo.png";

  useEffect(() => {
    fetchPlayerData();
  }, []);

  const fetchPlayerData = async () => {
    try {
      const [
        playerRes, 
        fixturesRes, 
        statsRes, 
        paymentsRes, 
        feedbackRes
      ] = await Promise.all([
        // Try to fetch player profile data
        axios.get('/players/me').catch(() => ({ data: null })),
        axios.get('/fixtures').catch(() => ({ data: [] })),
        axios.get('/players/me/stats').catch(() => ({ data: null })),
        axios.get('/players/me/payments').catch(() => ({ data: [] })),
        axios.get('/players/me/feedback').catch(() => ({ data: [] }))
      ]);
      
      setPlayerData(playerRes.data);
      setFixtures(fixturesRes.data.slice(0, 5));
      setPlayerStats(statsRes.data);
      setPlayerPayments(paymentsRes.data);
      setCoachFeedback(feedbackRes.data);
      
      // Mock notifications for demo
      setNotifications([
        {
          id: 1,
          title: 'Training Session Tomorrow',
          message: 'Reminder: Training session at 7:00 PM at the main pitch.',
          date: new Date().toISOString(),
          read: false
        },
        {
          id: 2,
          title: 'Match Day Preparation',
          message: 'Team meeting 1 hour before kickoff this Saturday.',
          date: new Date(Date.now() - 86400000).toISOString(),
          read: true
        }
      ]);
      
      setProfileData({
        full_name: user?.full_name || '',
        email: user?.email || '',
        phone: '',
        emergency_contact: '',
        address: '',
        date_of_birth: '',
        position_preference: '',
        medical_notes: '',
        availability: {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: true,
          sunday: true
        }
      });
      
    } catch (error) {
      console.error('Error fetching player data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePaymentStatus = () => {
    const outstandingPayments = playerPayments.filter(p => p.status === 'outstanding' || p.status === 'overdue');
    const totalOutstanding = outstandingPayments.reduce((sum, payment) => sum + payment.amount, 0);
    
    return {
      isUpToDate: outstandingPayments.length === 0,
      totalOutstanding,
      outstandingCount: outstandingPayments.length,
      overduePayments: playerPayments.filter(p => p.status === 'overdue')
    };
  };

  const PaymentStatusDisplay = () => {
    const paymentStatus = calculatePaymentStatus();
    
    if (paymentStatus.isUpToDate) {
      return (
        <div className="flex items-center justify-center p-6 bg-green-50 rounded-lg border border-green-200">
          <Smile className="h-8 w-8 text-green-600 mr-3" />
          <div className="text-center">
            <h3 className="text-lg font-semibold text-green-800">Payments Up to Date!</h3>
            <p className="text-green-600">All your payments are current</p>
          </div>
        </div>
      );
    } else {
      return (
        <div className="p-6 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-center justify-center mb-4">
            <Frown className="h-8 w-8 text-red-600 mr-3" />
            <div className="text-center">
              <h3 className="text-lg font-semibold text-red-800">Outstanding Payments</h3>
              <p className="text-2xl font-bold text-red-600">£{paymentStatus.totalOutstanding.toFixed(2)}</p>
            </div>
          </div>
          {paymentStatus.overduePayments.length > 0 && (
            <div className="mt-4 p-3 bg-red-100 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-red-800 font-semibold">
                  {paymentStatus.overduePayments.length} payment(s) overdue
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      // Mock update - in real app this would save to backend
      console.log('Updating profile:', profileData);
      setEditingProfile(false);
      // Show success message
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const markNotificationRead = (id) => {
    setNotifications(notifications.map(notif => 
      notif.id === id ? { ...notif, read: true } : notif
    ));
  };

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: Target },
    { id: 'profile', name: 'My Profile', icon: User },
    { id: 'payments', name: 'Payments', icon: CreditCard },
    { id: 'feedback', name: 'Coach Feedback', icon: Star },
    { id: 'schedule', name: 'Schedule', icon: Calendar },
    { id: 'documents', name: 'Documents', icon: FileText },
    { id: 'communications', name: 'Messages', icon: MessageSquare },
    { id: 'stats', name: 'My Stats', icon: Activity }
  ];

  const upcomingFixtures = fixtures.filter(f => f.status === 'upcoming').slice(0, 3);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your player portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-6">
            <img 
              src={clubLogo} 
              alt="Crawley Athletic FC" 
              className="h-16 w-16"
            />
            <div>
              <h1 className="text-3xl font-bold">Players' Portal</h1>
              <p className="text-gray-200">Welcome back, {user?.full_name}</p>
              <div className="flex items-center mt-2 space-x-4">
                <span className="text-sm bg-white bg-opacity-20 px-3 py-1 rounded-full">
                  {playerData?.position || 'Player'}
                </span>
                {playerData?.jersey_number && (
                  <span className="text-sm bg-white bg-opacity-20 px-3 py-1 rounded-full">
                    #{playerData.jersey_number}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 bg-white rounded-lg p-2 shadow">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-black text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            {/* Payment Status - Prominent Display */}
            <PaymentStatusDisplay />

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="card text-center">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-black" />
                <h3 className="text-2xl font-bold text-black">{playerStats?.training_attendance?.toFixed(0) || 95}%</h3>
                <p className="text-gray-600">Training Attendance</p>
                <p className="text-sm text-gray-500">This Season</p>
              </div>
              
              <div className="card text-center">
                <Trophy className="h-12 w-12 mx-auto mb-4 text-black" />
                <h3 className="text-2xl font-bold text-black">{playerStats?.matches_played ?? 0}</h3>
                <p className="text-gray-600">Matches Played</p>
                <p className="text-sm text-gray-500">This Season</p>
              </div>
              
              <div className="card text-center">
                <Target className="h-12 w-12 mx-auto mb-4 text-black" />
                <h3 className="text-2xl font-bold text-black">{playerStats?.goals ?? 0}</h3>
                <p className="text-gray-600">Goals Scored</p>
                <p className="text-sm text-gray-500">This Season</p>
              </div>
              
              <div className="card text-center">
                <Activity className="h-12 w-12 mx-auto mb-4 text-black" />
                <h3 className="text-2xl font-bold text-black">{playerStats?.minutes_played ?? 0}</h3>
                <p className="text-gray-600">Minutes Played</p>
                <p className="text-sm text-gray-500">This Season</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Upcoming Fixtures */}
              <div className="card">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Upcoming Fixtures
                </h3>
                {upcomingFixtures.length > 0 ? (
                  <div className="space-y-4">
                    {upcomingFixtures.map((fixture) => (
                      <div key={fixture.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold">
                              {fixture.is_home ? 'vs' : 'at'} {fixture.away_team}
                            </h4>
                            <div className="flex items-center text-sm text-gray-600 mt-1">
                              <Clock className="h-4 w-4 mr-1" />
                              {fixture.match_date} at {fixture.match_time}
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <MapPin className="h-4 w-4 mr-1" />
                              {fixture.venue}
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                              {fixture.competition}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 text-center py-8">No upcoming fixtures scheduled</p>
                )}
              </div>

              {/* Recent Notifications */}
              <div className="card">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Bell className="h-5 w-5 mr-2" />
                  Recent Notifications
                </h3>
                <div className="space-y-4">
                  {notifications.map((notification) => (
                    <div 
                      key={notification.id} 
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        notification.read ? 'bg-gray-50' : 'bg-blue-50 border-blue-200'
                      }`}
                      onClick={() => markNotificationRead(notification.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">{notification.title}</h4>
                          <p className="text-gray-600 text-sm mt-1">{notification.message}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            {new Date(notification.date).toLocaleDateString()}
                          </p>
                        </div>
                        {!notification.read && (
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            <PaymentStatusDisplay />
            
            <div className="card">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                Payment History
              </h3>
              
              {playerPayments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Description</th>
                        <th className="text-left py-3 px-4">Amount</th>
                        <th className="text-left py-3 px-4">Due Date</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Paid Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {playerPayments.map((payment) => (
                        <tr key={payment.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium">{payment.description}</p>
                              <p className="text-sm text-gray-500 capitalize">{payment.payment_type}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 font-semibold">
                            £{payment.amount.toFixed(2)}
                          </td>
                          <td className="py-3 px-4">
                            {new Date(payment.due_date).toLocaleDateString('en-GB')}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              payment.status === 'paid' 
                                ? 'bg-green-100 text-green-800'
                                : payment.status === 'overdue'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {payment.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {payment.paid_date 
                              ? new Date(payment.paid_date).toLocaleDateString('en-GB')
                              : '-'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">No payment records found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Coach Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="card">
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Star className="h-5 w-5 mr-2" />
              Coach Feedback
            </h3>
            
            {coachFeedback.length > 0 ? (
              <div className="space-y-6">
                {coachFeedback.map((feedback) => (
                  <div key={feedback.id} className="border rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-lg">Coach Notes</h4>
                        <p className="text-sm text-gray-500">{feedback.category}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(feedback.date).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                      {feedback.rating && (
                        <div className="flex items-center">
                          <span className="text-2xl font-bold text-yellow-600 mr-2">
                            {feedback.rating}
                          </span>
                          <span className="text-sm text-gray-500">/10</span>
                        </div>
                      )}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">{feedback.feedback}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Star className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">No coach feedback available yet</p>
                <p className="text-sm text-gray-500 mt-2">
                  Your coaches will provide feedback after training sessions and matches
                </p>
              </div>
            )}
          </div>
        )}

        {/* Profile */}
        {activeTab === 'profile' && (
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">My Profile</h2>
                <button
                  onClick={() => setEditingProfile(!editingProfile)}
                  className="btn btn-primary"
                >
                  {editingProfile ? (
                    <>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </>
                  ) : (
                    <>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Profile
                    </>
                  )}
                </button>
              </div>

              {editingProfile ? (
                <form onSubmit={handleProfileUpdate} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label className="form-label">Full Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={profileData.full_name}
                        onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        className="form-input"
                        value={profileData.email}
                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        className="form-input"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        placeholder="+44 123 456 7890"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Date of Birth</label>
                      <input
                        type="date"
                        className="form-input"
                        value={profileData.date_of_birth}
                        onChange={(e) => setProfileData({ ...profileData, date_of_birth: e.target.value })}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Emergency Contact</label>
                      <input
                        type="text"
                        className="form-input"
                        value={profileData.emergency_contact}
                        onChange={(e) => setProfileData({ ...profileData, emergency_contact: e.target.value })}
                        placeholder="Name and phone number"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Preferred Position</label>
                      <select
                        className="form-input"
                        value={profileData.position_preference}
                        onChange={(e) => setProfileData({ ...profileData, position_preference: e.target.value })}
                      >
                        <option value="">Select Position</option>
                        <option value="Goalkeeper">Goalkeeper</option>
                        <option value="Defender">Defender</option>
                        <option value="Midfielder">Midfielder</option>
                        <option value="Forward">Forward</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Address</label>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      value={profileData.address}
                      onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                      placeholder="Full address"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Medical Notes</label>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      value={profileData.medical_notes}
                      onChange={(e) => setProfileData({ ...profileData, medical_notes: e.target.value })}
                      placeholder="Any medical conditions, allergies, or notes"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Weekly Availability</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                      {Object.keys(profileData.availability).map((day) => (
                        <div key={day} className="flex items-center">
                          <input
                            type="checkbox"
                            id={day}
                            checked={profileData.availability[day]}
                            onChange={(e) => setProfileData({
                              ...profileData,
                              availability: {
                                ...profileData.availability,
                                [day]: e.target.checked
                              }
                            })}
                            className="mr-2"
                          />
                          <label htmlFor={day} className="text-sm capitalize">
                            {day}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button type="submit" className="btn btn-primary">
                      <Save className="h-4 w-4 mr-2" />
                      Save Profile
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingProfile(false)}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-1">Full Name</h3>
                      <p className="text-gray-900">{profileData.full_name || 'Not set'}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-1">Email</h3>
                      <p className="text-gray-900">{profileData.email || 'Not set'}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-1">Phone</h3>
                      <p className="text-gray-900">{profileData.phone || 'Not set'}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-1">Date of Birth</h3>
                      <p className="text-gray-900">{profileData.date_of_birth || 'Not set'}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-1">Emergency Contact</h3>
                      <p className="text-gray-900">{profileData.emergency_contact || 'Not set'}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-1">Preferred Position</h3>
                      <p className="text-gray-900">{profileData.position_preference || 'Not set'}</p>
                    </div>
                  </div>
                  
                  {profileData.address && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-1">Address</h3>
                      <p className="text-gray-900">{profileData.address}</p>
                    </div>
                  )}
                  
                  {profileData.medical_notes && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-1">Medical Notes</h3>
                      <p className="text-gray-900">{profileData.medical_notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Schedule */}
        {activeTab === 'schedule' && (
          <div className="space-y-6">
            <div className="card">
              <h3 className="text-xl font-semibold mb-4">Training Schedule</h3>
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">Team Training</h4>
                      <p className="text-gray-600">Tuesday, 7:00 PM - 9:00 PM</p>
                      <p className="text-sm text-gray-500">Main Training Ground</p>
                    </div>
                    <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">
                      Regular
                    </span>
                  </div>
                </div>
                
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">Team Training</h4>
                      <p className="text-gray-600">Thursday, 7:00 PM - 9:00 PM</p>
                      <p className="text-sm text-gray-500">Main Training Ground</p>
                    </div>
                    <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">
                      Regular
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-semibold mb-4">Match Schedule</h3>
              <div className="space-y-4">
                {upcomingFixtures.map((fixture) => (
                  <div key={fixture.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">
                          {fixture.is_home ? 'vs' : 'at'} {fixture.away_team}
                        </h4>
                        <p className="text-gray-600">{fixture.match_date} at {fixture.match_time}</p>
                        <p className="text-sm text-gray-500">{fixture.venue}</p>
                      </div>
                      <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {fixture.competition}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Documents */}
        {activeTab === 'documents' && (
          <div className="card">
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <BookOpen className="h-5 w-5 mr-2" />
              Team Documents
            </h3>
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold">Training Manual 2024/25</h4>
                <p className="text-gray-600 text-sm">Updated training procedures and exercises</p>
                <p className="text-xs text-gray-500 mt-2">Uploaded 2 weeks ago</p>
              </div>
              
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold">Player Handbook</h4>
                <p className="text-gray-600 text-sm">Club rules, policies, and expectations</p>
                <p className="text-xs text-gray-500 mt-2">Uploaded 1 month ago</p>
              </div>
              
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold">Fitness Program</h4>
                <p className="text-gray-600 text-sm">Individual fitness routines and goals</p>
                <p className="text-xs text-gray-500 mt-2">Uploaded 3 days ago</p>
              </div>
            </div>
          </div>
        )}

        {/* Communications */}
        {activeTab === 'communications' && (
          <div className="card">
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <MessageSquare className="h-5 w-5 mr-2" />
              Team Messages
            </h3>
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-sm">From: Coach Smith</h4>
                    <h3 className="font-semibold mt-1">Match Preparation - Saturday</h3>
                    <p className="text-gray-600 text-sm mt-2">
                      Team, please ensure you arrive 90 minutes before kickoff for warm-up and team talk. 
                      Don't forget to bring both home and away kits.
                    </p>
                    <p className="text-xs text-gray-500 mt-3">2 days ago</p>
                  </div>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">New</span>
                </div>
              </div>
              
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-sm">From: Team Captain</h4>
                    <h3 className="font-semibold mt-1">Training Intensity This Week</h3>
                    <p className="text-gray-600 text-sm mt-2">
                      Great work in training everyone. Let's maintain this intensity as we prepare for the weekend match.
                    </p>
                    <p className="text-xs text-gray-500 mt-3">1 week ago</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="card text-center">
                <Trophy className="h-12 w-12 mx-auto mb-4 text-black" />
                <h3 className="text-3xl font-bold text-black">{playerStats?.matches_played ?? 0}</h3>
                <p className="text-gray-600">Matches Played</p>
              </div>
              
              <div className="card text-center">
                <Target className="h-12 w-12 mx-auto mb-4 text-black" />
                <h3 className="text-3xl font-bold text-black">{playerStats?.goals ?? 0}</h3>
                <p className="text-gray-600">Goals</p>
              </div>
              
              <div className="card text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-black" />
                <h3 className="text-3xl font-bold text-black">{playerStats?.assists ?? 0}</h3>
                <p className="text-gray-600">Assists</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card">
                <h3 className="text-xl font-semibold mb-4">Season Performance</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Matches Started</span>
                    <span className="font-semibold">{playerStats?.matches_started ?? 0} / {playerStats?.matches_played ?? 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Minutes Played</span>
                    <span className="font-semibold">{playerStats?.minutes_played ?? 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Training Attendance</span>
                    <span className="font-semibold">{playerStats?.training_attendance?.toFixed(0) || 95}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Goals per Match</span>
                    <span className="font-semibold">
                      {((playerStats?.goals ?? 0) / (playerStats?.matches_played ?? 1)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="text-xl font-semibold mb-4">Disciplinary Record</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-yellow-400 rounded mr-2"></div>
                      <span className="text-gray-600">Yellow Cards</span>
                    </div>
                    <span className="font-semibold text-2xl text-yellow-600">
                      {playerStats?.yellow_cards ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                      <span className="text-gray-600">Red Cards</span>
                    </div>
                    <span className="font-semibold text-2xl text-red-600">
                      {playerStats?.red_cards ?? 0}
                    </span>
                  </div>
                  
                  {(playerStats?.yellow_cards ?? 0) === 0 && (playerStats?.red_cards ?? 0) === 0 && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg text-center">
                      <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                      <p className="text-green-800 font-semibold">Clean Record!</p>
                      <p className="text-green-600 text-sm">No cards this season</p>
                    </div>
                  )}
                  
                  {((playerStats?.yellow_cards ?? 0) > 0 || (playerStats?.red_cards ?? 0) > 0) && (
                    <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 mb-2" />
                      <p className="text-yellow-800 text-sm">
                        Keep working on discipline to maintain a clean record
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayersArea;